import React, { useCallback, useState } from 'react';

import { FormApi, Validators } from './types';
import FormBuilder from './form-builder';

interface UseFormParams<T> {
  validators: Validators<T>;
}

// The props to the <Form /> component.
interface FormComponentProps {
  children: React.ReactNode;
  onSubmit: () => void;
}

/**
 * The hook that exposes the form functionality.
 *
 * It returns a form component that the client should wrap its form fields with,
 * and the rest of the API for accessing form data, errors and the rest of the FormApi.
 */
function useForm<T>({
  validators,
}: UseFormParams<T>): [React.FC<FormComponentProps>, FormApi<T>] {
  const [initialFormData, setInitialFormData] = useState({});
  const [formApi, setFormApi] = useState<FormApi<T>>({
    formData: {},
    formErrors: {},
    setFormData: setInitialFormData,
    validators: {},
    register: () => {},
    unregister: () => {},
  });

  const FormComponent: React.FC<FormComponentProps> = useCallback(
    ({ onSubmit, children }) => (
      <FormBuilder
        initialFormData={initialFormData}
        validators={validators}
        setFormApi={setFormApi}
        onSubmit={onSubmit}
      >
        {children}
      </FormBuilder>
    ),
    [initialFormData, validators],
  );

  return [FormComponent, formApi];
}

export default useForm;
